import { Meta, Title, MetaDefinition } from '@angular/platform-browser';
import { TransferState, makeStateKey } from '@angular/core';
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

export interface MetaTagConfig {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  keywords?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(
    private titleService: Title,
    private metaService: Meta,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /**
   * Sets metadata for the current page
   * @param config MetaTagConfig object containing metadata
   */
  updateMetaTags(config: MetaTagConfig): void {
    // Store metadata in TransferState for SSR
    if (isPlatformServer(this.platformId)) {
      this.setTransferStateData('metaTags', config);
    }

    // Update title
    this.titleService.setTitle(config.title);

    // Update meta tags
    const metaTags: MetaDefinition[] = [
      { name: 'description', content: config.description },
      { property: 'og:title', content: config.title },
      { property: 'og:description', content: config.description },
      { property: 'og:type', content: config.type || 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: config.title },
      { name: 'twitter:description', content: config.description }
    ];

    if (config.keywords) {
      metaTags.push({ name: 'keywords', content: config.keywords });
    }

    if (config.author) {
      metaTags.push({ name: 'author', content: config.author });
    }

    if (config.url) {
      metaTags.push({ property: 'og:url', content: config.url });
    }

    if (config.image) {
      metaTags.push(
        { property: 'og:image', content: config.image },
        { name: 'twitter:image', content: config.image }
      );
    }

    // Update all meta tags
    metaTags.forEach(tag => {
      this.metaService.updateTag(tag);
    });
  }

  /**
   * Store data in TransferState to avoid duplicate requests
   * @param key State key identifier
   * @param data Data to be stored
   */
  setTransferStateData<T>(keyName: string, data: T): void {
    const stateKey = makeStateKey<T>(keyName);
    this.transferState.set(stateKey, data);
  }

  /**
   * Retrieve data from TransferState
   * @param key State key identifier
   * @returns Data stored in TransferState or null if not found
   */
  getTransferStateData<T>(keyName: string): T | null {
    const stateKey = makeStateKey<T>(keyName);
    if (this.transferState.hasKey(stateKey)) {
      return this.transferState.get(stateKey, null as T);
    }
    return null;
  }
}
